/**
 * Token Cleanup Utility
 *
 * Removes old authentication tokens from localStorage, sessionStorage, and Redux persist.
 * This is a one-time cleanup utility for edge cases where old tokens may still exist.
 *
 * Note: After migration is complete, this utility can be simplified or removed.
 */

import { logError, logWarning } from "./errorLogger";

// Old token keys that should be cleaned up
const OLD_TOKEN_KEYS = [
  "mirsal_access_token",
  "mirsal_refresh_token",
  "access_token",
  "refresh_token",
  "token",
  "auth_token",
  "jwt_token",
  "mirsal_token",
  "decodedToken",
  "decoded_token",
];

// Migration flag key
const MIGRATION_FLAG_KEY = "mirsal_auth_migrated_to_cookies";

/**
 * Check if migration has already been completed
 */
export function isMigrationComplete(): boolean {
  try {
    return localStorage.getItem(MIGRATION_FLAG_KEY) === "true";
  } catch (error) {
    logError("Failed to check migration status", error);
    return false;
  }
}

/**
 * Mark migration as complete
 */
export function markMigrationComplete(): void {
  try {
    localStorage.setItem(MIGRATION_FLAG_KEY, "true");
  } catch (error) {
    logError("Failed to mark migration as complete", error);
  }
}

/**
 * Detect if old tokens exist in storage
 */
export function hasOldTokens(): boolean {
  try {
    // Check localStorage
    for (const key of OLD_TOKEN_KEYS) {
      if (localStorage.getItem(key)) {
        return true;
      }
    }

    // Check sessionStorage
    for (const key of OLD_TOKEN_KEYS) {
      if (sessionStorage.getItem(key)) {
        return true;
      }
    }

    // Check Redux persist storage
    const persistRoot = localStorage.getItem("persist:root");
    if (persistRoot) {
      try {
        const state = JSON.parse(persistRoot);
        if (state.auth) {
          const authState = JSON.parse(state.auth);
          if (
            authState.accessToken ||
            authState.refreshToken ||
            authState.tokens
          ) {
            return true;
          }
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }

    return false;
  } catch (error) {
    logError("Failed to detect old tokens", error);
    return false;
  }
}

/**
 * Clean up old tokens from localStorage and sessionStorage
 */
export function cleanupOldTokens(): {
  cleaned: string[];
  errors: string[];
} {
  const cleaned: string[] = [];
  const errors: string[] = [];

  try {
    // Clean localStorage
    for (const key of OLD_TOKEN_KEYS) {
      try {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          cleaned.push(`localStorage.${key}`);
        }
      } catch (error) {
        errors.push(`Failed to remove localStorage.${key}: ${error}`);
      }
    }

    // Clean sessionStorage
    for (const key of OLD_TOKEN_KEYS) {
      try {
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
          cleaned.push(`sessionStorage.${key}`);
        }
      } catch (error) {
        errors.push(`Failed to remove sessionStorage.${key}: ${error}`);
      }
    }

    // Clean Redux persist storage (remove tokens but keep other auth data)
    const persistRoot = localStorage.getItem("persist:root");
    if (persistRoot) {
      try {
        const state = JSON.parse(persistRoot);
        if (state.auth) {
          const authState = JSON.parse(state.auth);
          let modified = false;

          // Remove token-related fields
          if (authState.accessToken) {
            delete authState.accessToken;
            modified = true;
          }
          if (authState.refreshToken) {
            delete authState.refreshToken;
            modified = true;
          }
          if (authState.tokens) {
            delete authState.tokens;
            modified = true;
          }

          if (modified) {
            state.auth = JSON.stringify(authState);
            localStorage.setItem("persist:root", JSON.stringify(state));
            cleaned.push("persist:root.auth.tokens");
          }
        }
      } catch (error) {
        errors.push(`Failed to clean Redux persist: ${error}`);
      }
    }

    if (cleaned.length > 0) {
      logWarning(`[Token Cleanup] Cleaned ${cleaned.length} old token entries`, { cleaned });
    }

    if (errors.length > 0) {
      logError("Token cleanup errors", { errors });
    }

    return { cleaned, errors };
  } catch (error) {
    logError("Token cleanup failed", error);
    return { cleaned, errors: [...errors, `Cleanup failed: ${error}`] };
  }
}

/**
 * Clean up full user data from storage (migrate to minimal data)
 * This removes PII (email, phone) from stored user data
 */
export function cleanupFullUserData(): {
  cleaned: string[];
  errors: string[];
} {
  const cleaned: string[] = [];
  const errors: string[] = [];

  try {
    // Check and migrate user data in localStorage
    const localUserData = localStorage.getItem("mirsal_user_data");
    if (localUserData) {
      try {
        const user = JSON.parse(localUserData);
        // If user data contains email or phone, migrate to minimal data
        if (user.email || user.phone || user.created_at) {
          const minimalUser = {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            role: user.role,
            active_role: user.active_role || user.role,
            roles: user.roles || [user.role],
            is_active: user.is_active,
          };
          localStorage.setItem("mirsal_user_data", JSON.stringify(minimalUser));
          cleaned.push("localStorage.mirsal_user_data (migrated to minimal)");
        }
      } catch (error) {
        errors.push(`Failed to migrate localStorage user data: ${error}`);
      }
    }

    // Check and migrate user data in sessionStorage
    const sessionUserData = sessionStorage.getItem("mirsal_user_data");
    if (sessionUserData) {
      try {
        const user = JSON.parse(sessionUserData);
        // If user data contains email or phone, migrate to minimal data
        if (user.email || user.phone || user.created_at) {
          const minimalUser = {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            role: user.role,
            active_role: user.active_role || user.role,
            roles: user.roles || [user.role],
            is_active: user.is_active,
          };
          sessionStorage.setItem(
            "mirsal_user_data",
            JSON.stringify(minimalUser)
          );
          cleaned.push("sessionStorage.mirsal_user_data (migrated to minimal)");
        }
      } catch (error) {
        errors.push(`Failed to migrate sessionStorage user data: ${error}`);
      }
    }

    if (cleaned.length > 0) {
      logWarning(`[Security Cleanup] Migrated ${cleaned.length} user data entries to minimal format`, { cleaned });
    }

    return { cleaned, errors };
  } catch (error) {
    logError("User data cleanup failed", error);
    return { cleaned, errors: [...errors, `Cleanup failed: ${error}`] };
  }
}

/**
 * Perform complete migration cleanup
 * This should be called once per user after migration
 * @deprecated Use cleanupOldTokens() and cleanupFullUserData() directly instead
 */
export function performMigrationCleanup(): void {
  if (isMigrationComplete()) {
    // Still clean up user data even if migration is complete
    cleanupFullUserData();
    return; // Already migrated
  }

  if (!hasOldTokens()) {
    // Still clean up user data
    cleanupFullUserData();
    markMigrationComplete();
    return; // No old tokens to clean
  }

  const tokenResult = cleanupOldTokens();
  const userDataResult = cleanupFullUserData();

  const allCleaned = [...tokenResult.cleaned, ...userDataResult.cleaned];
  const allErrors = [...tokenResult.errors, ...userDataResult.errors];

  if (allErrors.length === 0 || allCleaned.length > 0) {
    markMigrationComplete();
    // Migration cleanup completed successfully
  } else {
    logError("Migration cleanup had errors", { errors: allErrors });
  }
}

/**
 * Aggressive security cleanup - removes tokens from storage immediately
 * This should be called on every app load to ensure tokens are never in storage
 * SECURITY: Tokens should ONLY be in httpOnly cookies, never in browser storage
 */
export function performSecurityCleanup(): {
  tokensRemoved: number;
  userDataMigrated: number;
} {
  const tokenResult = cleanupOldTokens();
  const userDataResult = cleanupFullUserData();

  const tokensRemoved = tokenResult.cleaned.length;
  const userDataMigrated = userDataResult.cleaned.length;

  if (tokensRemoved > 0 || userDataMigrated > 0) {
    logWarning(`[Security Cleanup] Removed ${tokensRemoved} token(s), migrated ${userDataMigrated} user data entry/entries`);
  }

  return {
    tokensRemoved,
    userDataMigrated,
  };
}
